import { useMemo } from "react";

const galleryImages = [
  {
    url: "https://images.pexels.com/photos/31649749/pexels-photo-31649749.jpeg",
    alt: "Thai noodle soup garnished with herbs and chili",
  },
  {
    url: "https://images.pexels.com/photos/27860371/pexels-photo-27860371.jpeg",
    alt: "Thai noodle soup in pink bowl with chopsticks",
  },
  {
    url: "https://images.pexels.com/photos/7413153/pexels-photo-7413153.jpeg",
    alt: "Savory Asian noodle soup with tender meat and dumplings",
  },
  {
    url: "https://images.pexels.com/photos/1275894/pexels-photo-1275894.jpeg",
    alt: "Beef noodle soup with herbs and vegetables",
  },
  {
    url: "https://images.pexels.com/photos/11551931/pexels-photo-11551931.jpeg",
    alt: "Noodle soup with greens on a wooden tray",
  },
];

export function Home() {
  const year = useMemo(() => new Date().getFullYear(), []);

  return (
    <div className="min-h-screen">
      {/* Navbar */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-border bg-primary/95 backdrop-blur supports-[backdrop-filter]:bg-primary/80">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <a href="#top" className="group flex items-center gap-3 text-primary-foreground">
            <img
              src="https://storage.stardeck.ai/images/6b2fd0cc-26c0-4b90-a932-6e1661045718.png"
              alt="Restaurant logo"
              className="h-9 w-auto"
            />
            <span className="hidden text-sm font-semibold tracking-wide sm:inline group-hover:opacity-90">
              BKK Duck Noodle House
            </span>
          </a>
          <nav className="flex items-center gap-2 text-sm font-medium">
            {[
              { href: "#about", label: "About" },
              { href: "#menu", label: "Menu" },
              { href: "#gallery", label: "Gallery" },
              { href: "#location", label: "Visit" },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="rounded-full bg-secondary/70 px-3 py-1.5 text-foreground/90 shadow-sm transition-transform hover:scale-[1.03] hover:bg-secondary"
              >
                {item.label}
              </a>
            ))}
            <a
              href="#reserve"
              className="rounded-full bg-accent px-3 py-1.5 text-accent-foreground shadow-sm transition-transform hover:scale-[1.04]"
            >
              Reserve
            </a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="bg-background pt-24 scroll-mt-20">
        <div className="mx-auto max-w-6xl px-6 py-12 md:py-16">
          <div className="grid overflow-hidden rounded-2xl border border-border bg-card md:grid-cols-2">
            <div className="p-8 md:p-12">
              <h1 className="font-display text-4xl md:text-5xl leading-tight text-foreground">
                BKK Duck Noodle House
              </h1>
              <p className="mt-4 max-w-prose text-base md:text-lg text-muted-foreground">
                Slow-roasted duck, rich herbal broth, handmade noodles. A Bangkok classic, crafted
                with care.
              </p>
              <div className="mt-8 flex items-center gap-3">
                <a
                  href="#menu"
                  className="rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-sm hover:scale-[1.02] transition-transform"
                >
                  View Menu
                </a>
                <a
                  href="#reserve"
                  className="rounded-full bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground shadow-sm hover:scale-[1.02] transition-transform"
                >
                  Reserve a Table
                </a>
              </div>
            </div>
            <figure className="relative h-64 w-full md:h-full">
              <img
                src="https://storage.stardeck.ai/images/89ea66ad-7346-4f51-b922-c7695c32d7d3.jpg"
                alt="Duck noodle bowl served with rich broth and herbs"
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
              />
            </figure>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="mx-auto max-w-6xl px-6 py-20 md:py-28 scroll-mt-24">
        <div className="grid gap-10 md:grid-cols-12 md:gap-16 items-center">
          <div className="md:col-span-7">
            <h2 className="font-display text-3xl md:text-4xl text-foreground">
              Bangkok roots. Neighborhood soul.
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Our broth simmers for 8 hours with star anise, cinnamon, and roasted aromatics. Ducks
              are marinated in soy, palm sugar, and garlic, then slow-roasted for a lacquered
              finish. Each bowl is assembled to order with your choice of noodles and house chili
              jam.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="rounded-xl bg-secondary p-6">
              <dl className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">Hours</dt>
                  <dd className="mt-1 text-sm">Tue–Sun</dd>
                  <dd className="text-sm">11:00–21:00</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">Phone</dt>
                  <dd className="mt-1 text-sm">+66 2 123 4567</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground">Address</dt>
                  <dd className="mt-1 text-sm">Sukhumvit, Bangkok</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* Menu */}
      <section id="menu" className="mx-auto max-w-6xl px-6 py-20 md:py-28 scroll-mt-24">
        <div className="md:flex md:items-end md:justify-between">
          <h2 className="font-display text-3xl md:text-4xl">Menu Highlights</h2>
          <p className="mt-3 md:mt-0 text-muted-foreground">
            Available in small or large with rice or egg noodles.
          </p>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <article className="rounded-xl bg-card border border-border p-6">
            <h3 className="font-medium">Signature Duck Noodle Soup</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Slow-roasted duck, herbal broth, morning glory, fried garlic.
            </p>
            <p className="mt-4 text-primary font-semibold">THB 120</p>
          </article>
          <article className="rounded-xl bg-card border border-border p-6">
            <h3 className="font-medium">Crispy Duck with Rice</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Crackling skin, plum sauce, pickled chili, jasmine rice.
            </p>
            <p className="mt-4 text-primary font-semibold">THB 160</p>
          </article>
          <article className="rounded-xl bg-card border border-border p-6">
            <h3 className="font-medium">Spicy Duck Tom Yum Noodles</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Lemongrass, lime, roasted chili jam, peanuts, herbs.
            </p>
            <p className="mt-4 text-primary font-semibold">THB 140</p>
          </article>
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="mx-auto max-w-6xl px-6 py-20 md:py-28 scroll-mt-24">
        <h2 className="font-display text-3xl md:text-4xl">From the Kitchen</h2>
        <div className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3">
          {galleryImages.map((img) => (
            <figure key={img.url} className="overflow-hidden rounded-lg">
              <img
                src={`${img.url}?auto=compress&cs=tinysrgb&w=900&h=900&dpr=1`}
                alt={img.alt}
                className="h-44 w-full object-cover sm:h-56 md:h-60 lg:h-72"
                loading="lazy"
              />
            </figure>
          ))}
        </div>
      </section>

      {/* Location & Hours */}
      <section id="location" className="mx-auto max-w-6xl px-6 py-20 md:py-28 scroll-mt-24">
        <div className="grid gap-8 md:grid-cols-2">
          <div>
            <h2 className="font-display text-3xl md:text-4xl">Visit Us</h2>
            <p className="mt-4 text-muted-foreground">
              123 Sukhumvit Soi 26, Khlong Tan, Khlong Toei, Bangkok 10110
            </p>
            <div className="mt-6 space-y-2 text-sm">
              <p>Tue–Sun: 11:00–21:00</p>
              <p>Closed Mondays</p>
              <p>Tel: +66 2 123 4567</p>
            </div>
            <div className="mt-6">
              <a
                href="#reserve"
                className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground"
              >
                Book a Table
              </a>
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-border">
            <iframe
              title="Map to BKK Duck Noodle House"
              className="h-80 w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=Sukhumvit%2026%20Bangkok&output=embed"
            />
          </div>
        </div>
      </section>

      {/* Reserve / Contact */}
      <section id="reserve" className="mx-auto max-w-3xl px-6 pb-24 md:pb-32 scroll-mt-24">
        <div className="rounded-2xl border border-border bg-secondary p-8 md:p-10">
          <h2 className="font-display text-3xl md:text-4xl">Reservations</h2>
          <p className="mt-3 text-muted-foreground">
            For parties of 6+, please call. Same‑day tables are often available.
          </p>
          <form
            className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => e.preventDefault()}
          >
            <input
              type="text"
              name="name"
              placeholder="Your name"
              className="h-11 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              aria-label="Name"
              required
            />
            <input
              type="tel"
              name="phone"
              placeholder="Phone number"
              className="h-11 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              aria-label="Phone"
              required
            />
            <input
              type="date"
              name="date"
              className="h-11 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              aria-label="Date"
              required
            />
            <input
              type="time"
              name="time"
              className="h-11 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              aria-label="Time"
              required
            />
            <input
              type="number"
              name="guests"
              min={1}
              max={12}
              placeholder="Guests"
              className="h-11 rounded-lg border border-input bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              aria-label="Guests"
              required
            />
            <button
              type="submit"
              className="h-11 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:opacity-95"
              onClick={() => {
                const tel = "+6621234567";
                window.location.href = `tel:${tel}`;
              }}
            >
              Request by Phone
            </button>
          </form>
          <p className="mt-4 text-sm text-muted-foreground">
            Or email us at{" "}
            <a href="mailto:hello@bkkducknoodle.com" className="underline">
              hello@bkkducknoodle.com
            </a>
          </p>
        </div>
      </section>

      <footer className="border-t border-border bg-card/60 py-10 text-sm">
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-6 sm:flex-row">
          <p className="text-muted-foreground">© {year} BKK Duck Noodle House — Bangkok</p>
          <nav className="flex gap-4 text-muted-foreground">
            <a href="#about" className="hover:text-foreground">
              About
            </a>
            <a href="#menu" className="hover:text-foreground">
              Menu
            </a>
            <a href="#location" className="hover:text-foreground">
              Visit
            </a>
            <a href="#reserve" className="hover:text-foreground">
              Reserve
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
